import { toast } from 'sonner';
import { checkAIContent } from './aiDetection';

// Interfaces for assessment data
export interface MCQAnswer {
  questionId: string;
  selectedOptionId: string;
}

export interface DescriptiveAnswer {
  questionId: string;
  answer: string;
  aiScore?: number;
}

export interface CandidateAssessment {
  candidateId: string;
  candidateName: string;
  jobId: string;
  jobTitle: string;
  mcqAnswers: Record<string, string>;
  descriptiveAnswers: Record<string, string>;
  personalityAnswers: Record<string, string>;
  mcqScore: number;
  descriptiveScore?: number;
  overallScore: number;
  aiEvaluation?: string;
  personalityEvaluation?: string;
  completedAt: string;
  cheatingFlags?: {
    tabSwitched?: boolean;
    blurred?: boolean;
    aiDetected?: boolean;
    timeExpired?: boolean;
  };
}

/**
 * Calculate MCQ score based on correct answers
 */
export const calculateMCQScore = (
  mcqAnswers: Record<string, string>, 
  mcqQuestions: any[]
): number => {
  if (!mcqAnswers || !mcqQuestions || mcqQuestions.length === 0) return 0;
  
  let correctCount = 0;
  let totalQuestions = mcqQuestions.length;
  
  for (const question of mcqQuestions) {
    const candidateAnswer = mcqAnswers[question.id];
    if (candidateAnswer && candidateAnswer === question.correctAnswer) {
      correctCount++;
    }
  }
  
  return Math.round((correctCount / totalQuestions) * 100);
};

/**
 * Perform AI evaluation of descriptive answers
 * In a production app, this would call an AI service API
 */
export const evaluateDescriptiveAnswers = async (
  descriptiveAnswers: Record<string, string>,
  descriptiveQuestions: any[]
): Promise<{ score: number; evaluation: string }> => {
  try {
    if (!descriptiveAnswers || !descriptiveQuestions || descriptiveQuestions.length === 0) {
      return { score: 0, evaluation: "No answers provided." };
    }
    
    // First check if any answers were AI-generated
    const aiDetectionResults = await Promise.all(
      Object.values(descriptiveAnswers).map(answer => checkAIContent(answer))
    );
    
    const hasAIContent = aiDetectionResults.some(result => result.isAIGenerated);
    
    // Simple scoring logic for demo purposes
    // In a real app, this would use a more sophisticated AI evaluation service
    let totalScore = 0;
    let answerEvaluations = [];
    
    for (const question of descriptiveQuestions) {
      const answer = descriptiveAnswers[question.id] || '';
      const questionText = question.question;
      
      // Basic metrics to evaluate answers for demo purposes
      const wordCount = answer.split(/\s+/).filter(Boolean).length;
      const sentenceCount = answer.split(/[.!?]+/).filter(s => s.trim().length > 0).length;
      let answerScore = 0;
      let answerEvaluation = '';
      
      // Very simple scoring based on length and structure
      // In a real app, this would be replaced with actual AI evaluation
      if (wordCount < 10) {
        answerScore = 0;
        answerEvaluation = "Answer is too short and lacks substance.";
      } else if (wordCount < 50) {
        answerScore = 40;
        answerEvaluation = "Answer is brief but lacks detail and comprehensive analysis.";
      } else if (wordCount < 100) {
        answerScore = 70;
        answerEvaluation = "Answer provides some relevant points but could be more thorough.";
      } else {
        answerScore = 90;
        answerEvaluation = "Answer is thorough and well-articulated.";
      }
      
      // Adjust for structure
      if (sentenceCount > 3 && sentenceCount / wordCount < 0.1) {
        answerScore += 10;
        answerEvaluation += " Good sentence structure and flow.";
      }
      
      // Cap at 100
      answerScore = Math.min(answerScore, 100);
      
      totalScore += answerScore;
      answerEvaluations.push(`Question: ${questionText}\n\nEvaluation: ${answerEvaluation} (Score: ${answerScore}/100)`);
    }
    
    const averageScore = Math.round(totalScore / descriptiveQuestions.length);
    
    // If AI content detected, reduce the score significantly
    const adjustedScore = hasAIContent ? Math.max(0, averageScore - 50) : averageScore;
    
    let evaluation = answerEvaluations.join("\n\n");
    
    if (hasAIContent) {
      evaluation = "⚠️ AI-GENERATED CONTENT DETECTED: Significant penalty applied to overall score.\n\n" + evaluation;
    }
    
    evaluation += `\n\nOverall assessment: ${
      adjustedScore >= 80 ? "Excellent understanding and communication skills." :
      adjustedScore >= 60 ? "Good grasp of concepts with room for improvement." :
      adjustedScore >= 40 ? "Basic understanding but lacks depth." :
      "Insufficient demonstration of knowledge."
    }`;
    
    return { score: adjustedScore, evaluation };
  } catch (error) {
    console.error("Error evaluating descriptive answers:", error);
    toast.error("Error evaluating answers");
    return { score: 0, evaluation: "Error occurred during evaluation." };
  }
};

/**
 * Evaluate personality and behavioral answers
 * In a real app, this would use more sophisticated AI analysis
 */
export const evaluatePersonalityAnswers = async (
  personalityAnswers: Record<string, string>,
  personalityQuestions: any[]
): Promise<{ evaluation: string }> => {
  try {
    if (!personalityAnswers || !personalityQuestions || personalityQuestions.length === 0) {
      return { evaluation: "No personality assessment data provided." };
    }
    
    // Check if answers were AI-generated
    const aiDetectionResults = await Promise.all(
      Object.values(personalityAnswers).map(answer => checkAIContent(answer))
    );
    
    const hasAIContent = aiDetectionResults.some(result => result.isAIGenerated);
    
    // Simple evaluation logic for demo purposes
    // In a real app, this would use a more sophisticated AI evaluation service
    let personality = {
      teamwork: 0,
      adaptability: 0,
      communication: 0,
      problemSolving: 0,
      leadership: 0,
      cultural: 0
    };
    
    let answerEvaluations = [];
    
    for (const question of personalityQuestions) {
      const answer = personalityAnswers[question.id] || '';
      const questionText = question.question;
      
      // Very basic keyword analysis for demonstration
      const wordCount = answer.split(/\s+/).filter(Boolean).length;
      const lowercaseAnswer = answer.toLowerCase();
      
      // Simple trait scoring based on keywords
      if (/team|collaborat|together|colleague|cooper/i.test(lowercaseAnswer)) {
        personality.teamwork += 2;
      }
      
      if (/adapt|change|flexible|adjust|learn/i.test(lowercaseAnswer)) {
        personality.adaptability += 2;
      }
      
      if (/communicat|listen|discuss|express|clarif/i.test(lowercaseAnswer)) {
        personality.communication += 2;
      }
      
      if (/solve|solution|challenge|approach|analyz/i.test(lowercaseAnswer)) {
        personality.problemSolving += 2;
      }
      
      if (/lead|initiat|guide|responsib|direct/i.test(lowercaseAnswer)) {
        personality.leadership += 2;
      }
      
      if (/value|cultur|divers|inclus|ethic/i.test(lowercaseAnswer)) {
        personality.cultural += 2;
      }
      
      // Add length-based bonus (up to 1 point per category)
      if (wordCount > 50) {
        Object.keys(personality).forEach(key => {
          personality[key as keyof typeof personality] += 1;
        });
      }
      
      // Cap scores at 10
      Object.keys(personality).forEach(key => {
        personality[key as keyof typeof personality] = Math.min(
          personality[key as keyof typeof personality], 
          10
        );
      });
      
      // Generate evaluation
      let traitEvaluation = "";
      if (wordCount < 20) {
        traitEvaluation = "Response is too brief to provide meaningful personality insights.";
      } else {
        traitEvaluation = "Demonstrates ";
        const traits = [];
        
        if (personality.teamwork > 5) traits.push("good teamwork abilities");
        if (personality.adaptability > 5) traits.push("adaptability");
        if (personality.communication > 5) traits.push("effective communication");
        if (personality.problemSolving > 5) traits.push("problem-solving skills");
        if (personality.leadership > 5) traits.push("leadership potential");
        if (personality.cultural > 5) traits.push("cultural awareness");
        
        traitEvaluation += traits.length > 0 
          ? traits.join(", ") + "." 
          : "limited evidence of key behavioral traits.";
      }
      
      answerEvaluations.push(`Question: ${questionText}\n\nEvaluation: ${traitEvaluation}`);
    }
    
    // If AI content detected, add a warning
    if (hasAIContent) {
      answerEvaluations.unshift("⚠️ AI-GENERATED CONTENT DETECTED: The candidate may not have provided authentic responses.");
    }
    
    // Generate overall personality profile
    const avgTeamwork = personality.teamwork;
    const avgAdaptability = personality.adaptability;
    const avgCommunication = personality.communication;
    const avgProblemSolving = personality.problemSolving;
    const avgLeadership = personality.leadership;
    const avgCultural = personality.cultural;
    
    const overallScore = (avgTeamwork + avgAdaptability + avgCommunication + 
                        avgProblemSolving + avgLeadership + avgCultural) / 6;
    
    let strengthsAndWeaknesses = "Strengths: ";
    const strengths = [];
    const weaknesses = [];
    
    if (avgTeamwork >= 7) strengths.push("teamwork");
    else if (avgTeamwork <= 3) weaknesses.push("teamwork");
    
    if (avgAdaptability >= 7) strengths.push("adaptability");
    else if (avgAdaptability <= 3) weaknesses.push("adaptability");
    
    if (avgCommunication >= 7) strengths.push("communication");
    else if (avgCommunication <= 3) weaknesses.push("communication");
    
    if (avgProblemSolving >= 7) strengths.push("problem-solving");
    else if (avgProblemSolving <= 3) weaknesses.push("problem-solving");
    
    if (avgLeadership >= 7) strengths.push("leadership");
    else if (avgLeadership <= 3) weaknesses.push("leadership");
    
    if (avgCultural >= 7) strengths.push("cultural fit");
    else if (avgCultural <= 3) weaknesses.push("cultural alignment");
    
    strengthsAndWeaknesses += strengths.length > 0 
      ? strengths.join(", ") 
      : "No clear strengths identified";
    
    strengthsAndWeaknesses += "\n\nAreas for development: ";
    strengthsAndWeaknesses += weaknesses.length > 0 
      ? weaknesses.join(", ") 
      : "No significant weaknesses identified";
    
    // Overall recommendation
    let recommendation = "\n\nOverall assessment: ";
    if (overallScore >= 7) {
      recommendation += "The candidate demonstrates strong behavioral and cultural fit. They are likely to work well within teams and adapt to the organization's environment.";
    } else if (overallScore >= 5) {
      recommendation += "The candidate shows moderate behavioral qualities. They may require some support in certain areas but generally appear suitable.";
    } else {
      recommendation += "The candidate's behavioral profile indicates potential challenges with fit. Consider additional interviews focused on behavioral aspects.";
    }
    
    // Combine all evaluations
    const evaluation = answerEvaluations.join("\n\n") + 
                      "\n\n--- PERSONALITY PROFILE ---\n\n" + 
                      strengthsAndWeaknesses + 
                      recommendation;
    
    return { evaluation };
  } catch (error) {
    console.error("Error evaluating personality answers:", error);
    toast.error("Error evaluating personality answers");
    return { evaluation: "Error occurred during personality evaluation." };
  }
};

/**
 * Calculate overall assessment score
 */
export const calculateOverallScore = (mcqScore: number, descriptiveScore: number): number => {
  // Weight: 60% MCQ, 40% descriptive
  return Math.round((mcqScore * 0.6) + (descriptiveScore * 0.4));
};

/**
 * Save assessment result to localStorage for demo purposes
 * In a production app, this would be saved to a database
 */
export const saveAssessmentResult = (assessment: CandidateAssessment): void => {
  try {
    // Get existing assessments
    const existingData = localStorage.getItem('candidateAssessments');
    const assessments = existingData ? JSON.parse(existingData) : [];
    
    // Add new assessment
    assessments.push(assessment);
    
    // Save back to localStorage
    localStorage.setItem('candidateAssessments', JSON.stringify(assessments));
  } catch (error) {
    console.error("Error saving assessment result:", error);
    toast.error("Error saving assessment data");
  }
};

/**
 * Get all candidate assessments
 */
export const getCandidateAssessments = (): CandidateAssessment[] => {
  try {
    const data = localStorage.getItem('candidateAssessments');
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error("Error retrieving assessment data:", error);
    toast.error("Error retrieving assessment data");
    return [];
  }
};

/**
 * Get assessment for a specific candidate
 */
export const getCandidateAssessment = (candidateId: string): CandidateAssessment | undefined => {
  const assessments = getCandidateAssessments();
  return assessments.find(a => a.candidateId === candidateId);
};
